/**
 * Outil « Points mobiles » — calculs (pur) : milieu et distance de deux
 * points à coordonnées entières, valeurs EXACTES (moitiés, racines
 * simplifiées : √45 = 3√5).
 */

import { rational, type ExactNumber } from '../../ui/exact';
import type { Pt } from '../../ui/frame';
import { isqrt, simplify } from '../root-square/roots';

export function midpoint(a: Pt, b: Pt): { x: ExactNumber; y: ExactNumber } {
  return { x: rational(a.x + b.x, 2), y: rational(a.y + b.y, 2) };
}

/** AB² = (xB − xA)² + (yB − yA)² */
export function distance2(a: Pt, b: Pt): number {
  return (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
}

/** AB exact : « 5 », « √37 », « 3√5 » (k√b simplifiée) */
export function distanceExact(
  a: Pt,
  b: Pt,
): {
  plain: string;
  tex: string;
  atoms: string[];
  value: number;
} {
  const d2 = distance2(a, b);
  const r = isqrt(d2);
  if (r * r === d2) return { plain: String(r), tex: String(r), atoms: [String(r)], value: r };
  const { k, b: rest } = simplify(d2);
  if (k === 1)
    return {
      plain: `√${d2}`,
      tex: `\\sqrt{${d2}}`,
      atoms: ['√', String(d2)],
      value: Math.sqrt(d2),
    };
  return {
    plain: `${k}√${rest}`,
    tex: `${k}\\sqrt{${rest}}`,
    atoms: [String(k), '√', String(rest)],
    value: Math.sqrt(d2),
  };
}

export interface Goal {
  text: string;
  check: (a: Pt, b: Pt) => boolean;
}

export const GOALS: readonly Goal[] = [
  {
    text: 'Place A et B pour que le milieu de [AB] soit I(1 ; 2).',
    check: (a, b) => a.x + b.x === 2 && a.y + b.y === 4,
  },
  {
    text: 'Fais AB = 5 avec un segment ni horizontal ni vertical.',
    check: (a, b) => distance2(a, b) === 25 && a.x !== b.x && a.y !== b.y,
  },
  { text: 'Fais AB = √2.', check: (a, b) => distance2(a, b) === 2 },
  { text: 'Fais AB = 2√5.', check: (a, b) => distance2(a, b) === 20 },
  {
    text: 'Place A et B pour que le milieu de [AB] soit l’origine, avec A en (−3 ; 2).',
    check: (a, b) => a.x === -3 && a.y === 2 && b.x === 3 && b.y === -2,
  },
];
